/**
 * Table selection for SQL connectors.
 *
 * What the user picks is stored as its inverse: `excluded_table_names`, the
 * tables they deliberately unchecked. The set that actually syncs is derived
 * as "every table the source has right now, minus the exclusions", so a table
 * added to the database after the last visit is included on its own, while an
 * unchecked table stays out until the user checks it again.
 *
 * `selected_table_names` is still written alongside it — the backend's
 * schema-fetch, profiling and ontology steps all read that key — but it is a
 * materialised result, not the source of truth.
 *
 * The backend mirrors this in `backend/services/table_selection.py` and
 * re-runs it at the start of every Build Data Model job, which is what covers
 * users who never open the picker at all.
 *
 * SQL only. Drive must never sweep in new files, and REST connectors expose a
 * fixed resource list that does not drift.
 */

export interface SelectionMetadata {
  selected_table_names?: string[];
  excluded_table_names?: string[];
}

export interface AutoIncludeConnector {
  category?: string;
  connector_metadata?: SelectionMetadata | null;
}

/** Connector categories that gain tables behind the user's back. */
export function supportsAutoInclude(category?: string): boolean {
  return category === 'sql';
}

/**
 * Both forms a table may have been saved under: "public.users" and "users".
 *
 * Selections written by older UI versions (and by the onboarding importer)
 * are sometimes bare table names, which is why the backend adapters match on
 * both. We do the same so a legacy list still lines up with the qualified
 * names `/resources` returns.
 */
function nameVariants(fullName: string): string[] {
  const dot = fullName.indexOf('.');
  return dot > 0 ? [fullName, fullName.substring(dot + 1)] : [fullName];
}

/**
 * Every form the given saved entries could be written in.
 *
 * Matching has to work in both directions: saved names are sometimes bare
 * while the source reports qualified ones, and Redshift's resource list does
 * the reverse — a bare `name` with the qualified form in `id`.
 */
function expandEntries(entries: string[]): Set<string> {
  const out = new Set<string>();
  for (const entry of entries) {
    out.add(entry);
    const dot = entry.indexOf('.');
    if (dot > 0) out.add(entry.substring(dot + 1));
  }
  return out;
}

function matchesAny(fullName: string, entries: Set<string>): boolean {
  return nameVariants(fullName).some(v => entries.has(v));
}

/**
 * The exclusion list to apply, seeding it on first contact.
 *
 * A connector that already carries `excluded_table_names` uses it as-is.
 * Otherwise this is its first time through: a saved selection means
 * everything outside it was a deliberate removal and becomes the exclusion
 * list (so nothing changes for connectors configured before this shipped),
 * while a connector that was never configured excludes nothing at all.
 */
export function deriveExclusions(
  availableIds: string[],
  metadata?: SelectionMetadata | null,
): string[] {
  const existing = metadata?.excluded_table_names;
  if (Array.isArray(existing)) return existing;

  const saved = metadata?.selected_table_names;
  if (!Array.isArray(saved) || saved.length === 0) return [];

  const savedSet = expandEntries(saved);
  return availableIds.filter(id => !matchesAny(id, savedSet)).sort();
}

/** Everything the source has right now, minus the exclusions. */
export function effectiveSelection(availableIds: string[], excluded: string[]): string[] {
  const excludedSet = expandEntries(excluded);
  return availableIds.filter(id => !matchesAny(id, excludedSet));
}

/**
 * The exclusions to persist after the user saves the picker.
 *
 * Anything visible-and-unchecked is excluded; anything checked is not. Tables
 * that were excluded but are no longer in the source are carried forward
 * untouched — a table the user removed should stay removed even if it is
 * dropped and later recreated.
 */
export function nextExclusions(
  availableIds: string[],
  selectedIds: string[],
  previousExcluded: string[],
): string[] {
  const availableSet = new Set(availableIds);
  const selectedSet = new Set(selectedIds);
  const stale = previousExcluded.filter(name => !availableSet.has(name));
  const unchecked = availableIds.filter(id => !selectedSet.has(id));
  return Array.from(new Set([...stale, ...unchecked])).sort();
}

/**
 * True when a SQL connector has never been configured, so every table is
 * included by default. Used on the Build Data Model page to mark such a
 * connector ready without having to query the source for a table count.
 */
export function includesAllTables(conn: AutoIncludeConnector): boolean {
  if (!supportsAutoInclude(conn.category)) return false;
  const meta = conn.connector_metadata;
  if (Array.isArray(meta?.excluded_table_names)) return false;
  return !meta?.selected_table_names?.length;
}
